import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  Camera,
  CloudUpload,
  Loader2,
  MapPin,
  MessageSquareText,
  Navigation,
  Pencil,
  Phone,
  Plus,
  RefreshCw,
  ShoppingBag,
  Undo2,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import Sheet from '@/features/chat/components/Sheet';
import { DebtText, ScreenHeader, Spinner, Stepper } from '@/components/ui';
import { apiErrorMessage, isNetworkError } from '@/lib/api';
import { clearDraft, draftHasWork, loadDraft, saveDraft, type PointDraft } from '@/lib/drafts';
import { GEO_DENIED_HINT, distanceMeters, formatDistance, getPosition, withinRadius } from '@/lib/geo';
import { flushQueue, useQueue } from '@/lib/queue';
import { SHIFT_KEY } from '@/lib/shift-cache';
import { useTrack } from '@/lib/track';
import { formatKop, formatQty, formatWhen, isFractionalUnit, uid } from '@/lib/format';
import { PAY_METHOD_LABEL, PAY_MODE_LABEL, closePoint, openPoint, reopenPoint, type GeoFix, type PointScreen as Screen, type VisitCloseBody } from '@/services/courier';
import { usePointScreen } from './usePointScreen';
import { PaySheet } from './PaySheet';
import { ExchangeSheet, type ExchangeFrom, type ExchangeTo } from './ExchangeSheet';
import { useVisitPhoto, type PhotoReady, type VisitPhoto } from './useVisitPhoto';
import { bodyOf, draftFromVisitLines, r3, returnableMap, usageOf, visitMoney } from './visit-math';

/** Сервер не принимает отметку при погрешности хуже этой. */
const MAX_GPS_ACCURACY_M = 250;

export default function PointScreen() {
  const { pointId = '' } = useParams();
  const navigate = useNavigate();
  const { data, isPending, isError } = usePointScreen(pointId);
  const queued = useQueue((s) => s.items.find((q) => q.kind === 'close' && q.pointId === pointId) ?? null);

  const back = (
    <button type="button" className="btn-icon" onClick={() => navigate('/')} aria-label="К маршруту">
      <ArrowLeft size={20} />
    </button>
  );

  if (isPending) {
    return (
      <>
        <ScreenHeader title="Точка" left={back} />
        <Spinner />
      </>
    );
  }
  if (isError || !data) {
    return (
      <>
        <ScreenHeader title="Точка" left={back} />
        <p className="mx-auto max-w-md px-6 py-16 text-center text-sm text-ink/55">Точка недоступна: смена завершена или точку убрали из маршрута.</p>
      </>
    );
  }

  const s = data.data;
  const p = s.point;
  const mapUrl =
    p.lat != null && p.lon != null
      ? `https://yandex.ru/maps/?rtext=~${p.lat},${p.lon}&rtt=auto`
      : p.address
        ? `https://yandex.ru/maps/?text=${encodeURIComponent(`${s.shift.cityName}, ${p.address}`)}`
        : null;
  return (
    <>
      <ScreenHeader
        title={p.name}
        subtitle={p.address ?? `${s.shift.cityName} · ${s.shift.routeName}`}
        left={back}
        right={
          <div className="flex">
            {mapUrl && (
              <a className="btn-icon" href={mapUrl} target="_blank" rel="noreferrer" aria-label="Маршрут до точки">
                <MapPin size={20} />
              </a>
            )}
            {p.phone && (
              <a className="btn-icon" href={`tel:${p.phone}`} aria-label="Позвонить на точку">
                <Phone size={20} />
              </a>
            )}
          </div>
        }
      />
      {queued && queued.kind === 'close' ? (
        <PendingView body={queued.body} screen={s} photoPending={!!queued.photoPending} />
      ) : s.visit?.status === 'DONE' ? (
        <DoneView screen={s} />
      ) : (
        <VisitForm key={`${s.shift.id}:${s.point.id}`} screen={s} offline={data.offline} />
      )}
    </>
  );
}

const MODES: Array<{ id: 'sale' | 'return' | 'exchange'; label: string; icon: LucideIcon; on: string; off: string }> = [
  { id: 'sale', label: 'Продажа', icon: ShoppingBag, on: 'bg-emerald-700 text-white', off: 'bg-white text-emerald-800 ring-1 ring-inset ring-emerald-700/35' },
  { id: 'return', label: 'Возврат', icon: Undo2, on: 'bg-red-700 text-white', off: 'bg-white text-red-800 ring-1 ring-inset ring-red-700/35' },
  { id: 'exchange', label: 'Обмен', icon: ArrowLeftRight, on: 'bg-blue-700 text-white', off: 'bg-white text-blue-800 ring-1 ring-inset ring-blue-700/35' },
];

function ModeBar({ mode, onChange }: { mode: 'sale' | 'return' | 'exchange'; onChange: (mode: 'sale' | 'return' | 'exchange') => void }) {
  return (
    <div className="grid grid-cols-3 gap-2" role="tablist" aria-label="Что делаем на точке">
      {MODES.map((item) => {
        const Icon = item.icon;
        const active = mode === item.id;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={clsx(
              'flex min-h-11 items-center justify-center gap-1.5 rounded-xl px-2 text-[14px] font-medium transition-colors duration-150',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink',
              active ? item.on : `${item.off} hover:bg-ink/[0.03]`,
            )}
          >
            <Icon size={16} strokeWidth={2.25} />
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Набор товаров ──────────────────────────────────────────────────────────

function initialDraft(screen: Screen): PointDraft {
  const saved = loadDraft(screen.shift.id, screen.point.id);
  if (saved) return saved;
  // Точку открыли снова — отгрузка, замены и обмены прошлого закрытия остаются, правим их.
  const { lines, swaps } = draftFromVisitLines(screen.visit?.lines ?? []);
  const method = screen.visit?.paymentMethod;
  return { lines, swaps, paid: '', method: method && method !== 'DEBT' ? method : 'CASH', comment: screen.visit?.comment ?? '' };
}

type Stage = 'geo' | 'photo' | 'save';
const STAGE_LABEL: Record<Stage, string> = {
  geo: 'Определяем местоположение…',
  photo: 'Отправляем фото…',
  save: 'Сохраняем…',
};

function VisitForm({ screen, offline }: { screen: Screen; offline: boolean }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const addToQueue = useQueue((s) => s.add);
  const [draft, setDraft] = useState<PointDraft>(() => initialDraft(screen));
  const [payOpen, setPayOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [mode, setMode] = useState<'sale' | 'return' | 'exchange'>('sale');
  const beyond = screen.sellBeyondLoad === true;
  const [stage, setStage] = useState<Stage | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [requestId] = useState(uid);
  const photo = useVisitPhoto(screen.shift.id, screen.point.id, screen.visit?.photoUrl ?? null);
  const photoRequired = screen.photoRequired !== false;
  const gpsRequired = screen.gpsRequired !== false;

  // Пустой черновик не храним: просто открыть точку и выйти — не значит начать её.
  useEffect(() => {
    if (draftHasWork(draft)) saveDraft(screen.shift.id, screen.point.id, draft);
    else clearDraft(screen.shift.id, screen.point.id);
  }, [draft, screen.shift.id, screen.point.id]);

  // Время прибытия на точку пишем по первому действию на ней, а не по просмотру.
  const started = useRef(!!screen.visit);
  const touch = () => {
    if (started.current || offline) return;
    started.current = true;
    openPoint(screen.point.id)
      .then(() => qc.invalidateQueries({ queryKey: SHIFT_KEY }))
      .catch(() => {
        started.current = false;
      });
  };

  const menuById = useMemo(() => new Map(screen.menu.map((m) => [m.id, m])), [screen.menu]);
  const returnable = useMemo(() => returnableMap(screen, offline), [screen, offline]);
  const nameOf = useMemo(() => {
    const m = new Map<string, { name: string; unit: string }>();
    for (const r of screen.returnables) m.set(r.id, r);
    for (const r of screen.menu) m.set(r.id, r);
    return m;
  }, [screen.returnables, screen.menu]);

  const usage = usageOf(draft);
  /** Остаток в машине после набранного; null — без накладной. */
  const carLeft = (id: string) => {
    const m = menuById.get(id);
    return m?.inCar == null ? null : r3(m.inCar - (usage.out.get(id) ?? 0));
  };
  /** Сколько ещё можно забрать у точки; null — не знаем. */
  const takeLeft = (id: string) => (returnable == null ? null : r3((returnable.get(id) ?? 0) - (usage.taken.get(id) ?? 0)));

  const priceOf = (id: string) => menuById.get(id)?.price ?? screen.returnables.find((r) => r.id === id)?.price ?? 0;
  const money = visitMoney(draft, priceOf);
  const delivered = money.delivered;
  const swappedUnits = r3([...usage.taken.values()].reduce((a, b) => a + b, 0));
  const payMode = screen.payMode ?? 'FREE';
  const toPay = payMode === 'DEBT' ? 0 : Math.max(0, screen.point.debt + delivered);

  const setLine = (id: string, patch: Partial<{ d: number; r: number }>) => {
    touch();
    setDraft((cur) => {
      const next = { ...(cur.lines[id] ?? { d: 0, r: 0 }), ...patch };
      const lines = { ...cur.lines };
      if (next.d <= 0 && next.r <= 0) delete lines[id];
      else lines[id] = next;
      return { ...cur, lines };
    });
  };

  const exchangeFrom: ExchangeFrom[] = offline
    ? screen.menu.map((m) => ({ id: m.id, name: m.name, unit: m.unit, left: null }))
    : screen.returnables.map((r) => ({ id: r.id, name: r.name, unit: r.unit, left: Math.max(0, takeLeft(r.id) ?? 0) })).filter((r) => (r.left ?? 0) > 0);
  // «Сверх накладной» — выдать взамен можно и то, чего в машине уже нет: недостающее спишется со склада.
  const exchangeTo: ExchangeTo[] = screen.menu.map((m) => ({ id: m.id, name: m.name, unit: m.unit, left: beyond ? null : carLeft(m.id) }));
  const canExchange = exchangeFrom.length > 0 && exchangeTo.some((t) => (t.left == null || t.left > 0) && exchangeFrom.some((f) => f.id !== t.id));

  const fail = (message: string) => {
    setPayError(message);
    setStage(null);
  };

  const submit = async (paidAmount: number) => {
    setPayError(null);
    if (photoRequired && !photo.has) return fail('Сфотографируйте витрину — без фото точку не отметить');

    let geo: GeoFix | undefined;
    if (gpsRequired) {
      setStage('geo');
      try {
        geo = await getPosition();
      } catch (err) {
        return fail(err instanceof Error ? err.message : 'Не удалось определить местоположение');
      }
      if ((geo.accuracy ?? 0) > MAX_GPS_ACCURACY_M) {
        return fail(`Слабый сигнал GPS (±${geo.accuracy} м). Подождите несколько секунд или выйдите на улицу`);
      }
      const near = withinRadius(screen.point, geo, screen.radiusM);
      if (!near.ok && near.distance != null) {
        return fail(`Вы в ${formatDistance(near.distance)} от точки. Отметить её можно в радиусе ${screen.radiusM} м — подъедьте ближе`);
      }
    }

    let ready: PhotoReady = 'uploaded';
    if (photoRequired) {
      setStage('photo');
      ready = await photo.ensureReady();
      if (ready === 'missing') return fail('Сфотографируйте витрину ещё раз');
    }

    const body: VisitCloseBody = {
      ...bodyOf(draft, new Set(menuById.keys())),
      ...(geo ? { geo } : {}),
      paidAmount,
      // Режим магазина закрепил руководитель: при «только наличка» — наличные, при «в долг» способ не важен.
      paymentMethod:
        payMode === 'FREE' && screen.payMethods?.length && !screen.payMethods.includes(draft.method) ? screen.payMethods[0]! : payMode === 'FREE' ? draft.method : 'CASH',
      comment: draft.comment.trim() || null,
      clientRequestId: requestId,
      at: new Date().toISOString(),
    };
    const queueIt = (photoPending: boolean) => {
      addToQueue({
        id: requestId,
        kind: 'close',
        shiftId: screen.shift.id,
        pointId: screen.point.id,
        pointName: screen.point.name,
        body,
        createdAt: Date.now(),
        photoPending,
      });
      clearDraft(screen.shift.id, screen.point.id);
      toast('Нет связи — точка сохранена и уйдёт, как появится сеть', { duration: 5000 });
      navigate('/');
    };
    if (ready === 'pending') return queueIt(true);

    setStage('save');
    try {
      await closePoint(screen.point.id, body);
      clearDraft(screen.shift.id, screen.point.id);
      toast.success('Точка выполнена');
      void qc.invalidateQueries({ queryKey: SHIFT_KEY });
      void qc.invalidateQueries({ queryKey: ['point', screen.point.id] });
      navigate('/');
    } catch (err) {
      if (isNetworkError(err)) queueIt(false);
      else fail(apiErrorMessage(err));
      return;
    }
    setStage(null);
  };

  const openPay = () => {
    touch();
    if (photoRequired && !photo.has) {
      photo.openCamera();
      return;
    }
    setPayError(null);
    setPayOpen(true);
  };

  return (
    <>
      <div className="mx-auto w-full max-w-md space-y-3 px-4 pb-[calc(112px+env(safe-area-inset-bottom))] pt-3">
        <ModeBar mode={mode} onChange={setMode} />
        {(screen.hours || (screen.payMode && screen.payMode !== 'FREE')) && (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1 text-[13px] text-ink/55">
            {screen.payMode && screen.payMode !== 'FREE' && (
              <span
                className={clsx(
                  'rounded-full px-2 py-0.5 text-[12px] font-medium',
                  screen.payMode === 'DEBT' ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800',
                )}
              >
                {PAY_MODE_LABEL[screen.payMode]}
              </span>
            )}
            {screen.hours && <span>{screen.hours}</span>}
          </p>
        )}
        {screen.point.comment && (
          <p className="flex gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-[14px] text-amber-900">
            <MessageSquareText size={18} className="mt-0.5 shrink-0" />
            {screen.point.comment}
          </p>
        )}
        {gpsRequired ? (
          <GeoLine point={screen.point} radiusM={screen.radiusM} />
        ) : (
          <p className="px-1 text-[13px] text-ink/50">Отгрузка без GPS — место не проверяется</p>
        )}
        {screen.point.debt !== 0 && (
          <section className="card flex items-center justify-between gap-3 p-4">
            <div>
              <div className="text-[13px] text-ink/50">{screen.point.debt > 0 ? 'Долг точки' : 'Переплата точки'}</div>
              <div className="text-[13px] text-ink/50">{screen.point.debt > 0 ? 'Можно забрать при оплате' : 'Зачтётся в этом визите'}</div>
            </div>
            <DebtText value={screen.point.debt} className="text-xl font-semibold" />
          </section>
        )}
        {photoRequired && <PhotoCard photo={photo} onCapture={touch} />}
        {offline && (
          <p className="rounded-2xl bg-paper px-4 py-3 text-[13px] text-ink/60 ring-1 ring-line">
            Нет связи: меню из сохранённой смены. Сколько точка может вернуть, проверим при отправке.
          </p>
        )}

        {mode !== 'exchange' && (screen.menu.length === 0 ? (
          <div className="card px-6 py-10 text-center text-sm text-ink/55">
            {screen.shift.loadNumber ? 'В накладной не осталось товара на продажу.' : 'В меню маршрута нет товаров. Попросите руководителя добавить их.'}
          </div>
        ) : (
          <ul className="card list-rows overflow-hidden">
            {screen.shift.loadNumber && (
              <li className="bg-paper/70 px-4 py-2 text-[12px] font-medium text-ink/50">
                {beyond
                  ? `Накладная №${screen.shift.loadNumber} · можно отдать больше, лишнее спишется со склада`
                  : `Накладная №${screen.shift.loadNumber} · отдать можно только то, что в машине`}
              </li>
            )}
            {screen.menu.map((m) => {
              const line = draft.lines[m.id] ?? { d: 0, r: 0 };
              const fractional = isFractionalUnit(m.unit);
              const car = carLeft(m.id);
              const take = takeLeft(m.id);
              const capped = !beyond && car != null;
              const maxD = capped ? r3(line.d + Math.max(0, car)) : undefined;
              const limits = [beyond || car == null ? null : line.r + Math.max(0, car), take == null ? null : line.r + Math.max(0, take)].filter((x): x is number => x != null);
              const maxR = limits.length ? r3(Math.min(...limits)) : undefined;
              const selling = mode === 'sale';
              return (
                <li key={m.id} className="px-4 py-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 text-[15px] font-medium text-ink">{m.name}</span>
                    <span className="shrink-0 text-[13px] tabular-nums text-ink/50">
                      {formatKop(m.price)} / {m.unit}
                    </span>
                  </div>
                  {selling && car != null && (
                    <div className={clsx('mt-0.5 text-[12px] tabular-nums', !beyond && car <= 0 ? 'text-amber-800' : 'text-ink/45')}>
                      {!beyond && car <= 0 ? 'В машине больше нет' : `В машине ${formatQty(car)} ${m.unit}`}
                    </div>
                  )}
                  <div className="mt-2">
                    {selling ? (
                      <Stepper
                        label={`${m.name}: продал`}
                        value={line.d}
                        max={maxD}
                        fractional={fractional}
                        disabled={maxD === 0 && line.d === 0}
                        onChange={(v) => setLine(m.id, { d: v })}
                      />
                    ) : (
                      <Stepper
                        label={`${m.name}: возврат`}
                        value={line.r}
                        max={maxR}
                        fractional={fractional}
                        tone="return"
                        disabled={maxR === 0 && line.r === 0}
                        onChange={(v) => setLine(m.id, { r: v })}
                      />
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        ))}

        {mode === 'exchange' && (
        <section className="card p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-[15px] font-medium text-ink">Обмен</h2>
              <p className="text-[13px] text-ink/50">Забрал одно — отдал другое, деньги не меняются</p>
            </div>
            <button type="button" className="inline-flex h-9 shrink-0 items-center gap-1 rounded-lg bg-blue-700 px-3 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-40" onClick={() => setExchangeOpen(true)} disabled={!canExchange}>
              <Plus size={15} /> Обмен
            </button>
          </div>
          {draft.swaps.length > 0 && (
            <ul className="mt-3 space-y-2">
              {draft.swaps.map((sw, i) => {
                const from = nameOf.get(sw.fromItemId);
                const to = nameOf.get(sw.toItemId);
                return (
                  <li key={i} className="flex items-center gap-2 rounded-xl bg-amber-50 py-2 pl-3 pr-1 text-[14px]">
                    <span className="min-w-0 flex-1">
                      <span className="text-ink">
                        {from?.name ?? 'Товар'} · {formatQty(sw.qty)} {from?.unit ?? ''}
                      </span>
                      <ArrowRight size={14} className="mx-1 inline text-ink/40" />
                      <span className="text-ink">
                        {to?.name ?? 'Товар'} · {formatQty(sw.toQty)} {to?.unit ?? ''}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="btn-icon h-9 w-9"
                      aria-label="Убрать обмен"
                      onClick={() => setDraft((cur) => ({ ...cur, swaps: cur.swaps.filter((_, j) => j !== i) }))}
                    >
                      <X size={16} />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {!canExchange && draft.swaps.length === 0 && !offline && screen.returnables.length === 0 && (
            <p className="mt-2 text-[13px] text-ink/45">За 45 дней на точку не отгружали товар — забирать нечего.</p>
          )}
        </section>
        )}

        {screen.recent.length > 0 && (
          <section className="card p-4">
            <h2 className="mb-2 text-[13px] font-medium text-ink/50">Прошлые визиты</h2>
            <ul className="space-y-2">
              {screen.recent.map((r) => (
                <li key={r.id} className="flex items-baseline justify-between gap-3 text-[13px]">
                  <span className="min-w-0 truncate text-ink/70">
                    {r.closedAt ? formatWhen(r.closedAt) : '—'} · {r.courier.name}
                  </span>
                  <span className="shrink-0 tabular-nums text-ink/60">
                    {formatKop(r.deliveredTotal)} / получено {formatKop(r.paidAmount)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      <div className="pb-safe fixed inset-x-0 bottom-0 z-30 border-t border-line bg-white">
        <div className="mx-auto flex max-w-md items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] tabular-nums text-ink/50">
              Отгрузка {formatKop(delivered)}
              {money.taken > 0 && ` · возврат/обмен ${formatKop(money.taken)}`}
              {screen.point.debt !== 0 && ` · долг ${formatKop(screen.point.debt)}`}
            </div>
            <div className="text-lg font-semibold tabular-nums text-ink">
              {payMode === 'DEBT' ? `В долг ${formatKop(delivered)}` : `К оплате ${formatKop(toPay)}`}
            </div>
          </div>
          <button type="button" className="btn-primary px-5" onClick={openPay} disabled={photoRequired && photo.preparing}>
            {!photoRequired || photo.has ? (
              'Оплата'
            ) : (
              <>
                <Camera size={18} /> Фото
              </>
            )}
          </button>
        </div>
      </div>

      {payOpen && (
        <PaySheet
          money={money}
          swappedUnits={swappedUnits}
          debt={screen.point.debt}
          payMode={payMode}
          payMethods={screen.payMethods}
          debtLimit={screen.debtLimit}
          otherDebt={screen.otherDebt}
          draft={draft}
          busy={stage != null}
          busyLabel={stage ? STAGE_LABEL[stage] : null}
          error={payError}
          onDraft={(patch) => setDraft((cur) => ({ ...cur, ...patch }))}
          onClose={() => stage == null && setPayOpen(false)}
          onSubmit={submit}
        />
      )}
      {exchangeOpen && (
        <ExchangeSheet
          from={exchangeFrom}
          to={exchangeTo}
          onAdd={(sw) => {
            touch();
            setDraft((cur) => ({ ...cur, swaps: [...cur.swaps, sw] }));
          }}
          onClose={() => setExchangeOpen(false)}
        />
      )}
    </>
  );
}

// ─── Фото витрины ───────────────────────────────────────────────────────────

function PhotoCard({ photo, onCapture }: { photo: VisitPhoto; onCapture: () => void }) {
  return (
    <section className="card overflow-hidden">
      {photo.previewUrl && (
        <div className="relative bg-paper">
          <img src={photo.previewUrl} alt="Витрина" className="aspect-[4/3] w-full object-cover" />
          {photo.progress != null && (
            <div className="absolute inset-x-0 bottom-0 h-1 bg-ink/10">
              <div className="h-full bg-orange transition-[width]" style={{ width: `${Math.round(photo.progress * 100)}%` }} />
            </div>
          )}
        </div>
      )}
      {photo.previewUrl ? (
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="min-w-0 flex-1 text-[13px]">
            {photo.progress != null ? (
              <span className="text-ink/60">Отправляем фото… {Math.round(photo.progress * 100)} %</span>
            ) : photo.pendingUpload ? (
              <span className="text-amber-800">Нет связи — фото уйдёт вместе с точкой</span>
            ) : (
              <span className="text-emerald-700">Фото витрины сохранено</span>
            )}
          </div>
          <button type="button" className="btn-outline btn-sm shrink-0" onClick={photo.openCamera} disabled={photo.preparing || photo.progress != null}>
            <RefreshCw size={15} /> Переснять
          </button>
        </div>
      ) : (
        <button type="button" className="flex w-full items-center gap-3 p-4 text-left active:bg-ink/[0.03]" onClick={photo.openCamera} disabled={photo.preparing}>
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
            {photo.preparing ? <Loader2 size={22} className="animate-spin" /> : <Camera size={22} />}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-medium text-ink">Сфотографируйте витрину</span>
            <span className="block text-[13px] text-ink/50">Без фото точку не отметить</span>
          </span>
        </button>
      )}
      <input
        ref={photo.inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          if (e.target.files?.[0]) onCapture();
          void photo.onFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </section>
  );
}

/** Где курьер относительно точки — по живому GPS. */
function GeoLine({ point, radiusM }: { point: Screen['point']; radiusM: number }) {
  const status = useTrack((s) => s.status);
  const pos = useTrack((s) => s.position);
  if (status === 'denied') {
    return <p className="rounded-2xl bg-red-50 px-4 py-3 text-[13px] text-red-800">Нет доступа к геопозиции — точку не отметить. {GEO_DENIED_HINT}</p>;
  }
  let text: string;
  let tone: 'ok' | 'far' | 'muted' = 'muted';
  if (point.lat == null || point.lon == null) text = 'Координаты точки запишутся при первой отметке';
  else if (!pos) text = 'Ищем GPS…';
  else {
    const d = distanceMeters({ lat: point.lat, lon: point.lon }, pos);
    const ok = d <= radiusM + Math.min(pos.accuracy, 100);
    tone = ok ? 'ok' : 'far';
    text = ok ? `Вы у точки · ${formatDistance(d)}` : `До точки ${formatDistance(d)} — отметить можно в радиусе ${radiusM} м`;
  }
  return (
    <p className={clsx('flex items-center gap-2 px-1 text-[13px]', tone === 'ok' ? 'text-emerald-700' : tone === 'far' ? 'text-amber-800' : 'text-ink/50')}>
      <Navigation size={14} className="shrink-0" />
      {text}
    </p>
  );
}

// ─── Точка уже выполнена ────────────────────────────────────────────────────

function DoneView({ screen }: { screen: Screen }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const v = screen.visit!;

  const reopen = async () => {
    setBusy(true);
    try {
      const next = await reopenPoint(screen.point.id);
      qc.setQueryData(['point', screen.point.id], { data: next, offline: false });
      void qc.invalidateQueries({ queryKey: SHIFT_KEY });
      setConfirm(false);
    } catch {
      /* тост уже показан */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-md space-y-3 px-4 py-4">
      <section className="card p-4">
        <div className="flex items-center justify-between">
          <span className="badge-green">Выполнена{v.closedAt ? ` · ${formatWhen(v.closedAt)}` : ''}</span>
          <button type="button" className="btn-ghost btn-sm" onClick={() => setConfirm(true)}>
            <Pencil size={15} /> Изменить
          </button>
        </div>
        <dl className="mt-4 space-y-2 text-[15px]">
          <Row label="Отгружено" value={formatKop(v.deliveredTotal)} />
          <Row label={`Получено${v.paymentMethod && v.paidAmount > 0 ? ` · ${PAY_METHOD_LABEL[v.paymentMethod].toLowerCase()}` : ''}`} value={formatKop(v.paidAmount)} />
          <div className="border-t border-line pt-2">
            <Row label="Долг точки после визита" value={<DebtText value={v.debtAfter} />} strong />
          </div>
        </dl>
        {v.comment && <p className="mt-3 text-[13px] text-ink/55">«{v.comment}»</p>}
        {v.distanceM != null && (
          <p className="mt-3 flex items-center gap-1.5 text-[13px] text-ink/50">
            <Navigation size={13} /> Отмечена в {formatDistance(v.distanceM)} от точки
          </p>
        )}
      </section>
      {v.photoUrl && (
        <a href={v.photoUrl} target="_blank" rel="noreferrer" className="card block overflow-hidden">
          <img src={v.photoUrl} alt="Витрина" className="aspect-[4/3] w-full bg-paper object-cover" />
        </a>
      )}
      {v.lines.length > 0 && (
        <ul className="card list-rows overflow-hidden">
          {v.lines.map((l, i) => (
            <li key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-[14px]">
              <span className="min-w-0 truncate text-ink">{l.nameSnapshot}</span>
              <span className="shrink-0 text-right tabular-nums text-ink/60">
                {[
                  l.deliveredQty > 0 && `${formatQty(l.deliveredQty)} ${l.unitSnapshot}`,
                  l.returnedQty > 0 && <span className="text-amber-700">забрал {formatQty(l.returnedQty)}</span>,
                  l.replacedQty > 0 && <span className="text-amber-700">взамен {formatQty(l.replacedQty)}</span>,
                ]
                  .filter(Boolean)
                  .map((x, j) => (
                    <span key={j}>
                      {j > 0 && ' · '}
                      {x}
                    </span>
                  ))}
              </span>
            </li>
          ))}
        </ul>
      )}
      {confirm && (
        <Sheet title="Изменить точку?" onClose={() => !busy && setConfirm(false)}>
          <p className="text-[15px] text-ink/70">Продажа и обмены по этой точке отменятся, товар вернётся в машину. Поправьте и отметьте точку снова.</p>
          <button type="button" className="btn-primary mt-5 w-full" onClick={reopen} disabled={busy}>
            {busy && <Loader2 size={18} className="animate-spin" />}
            Открыть для правки
          </button>
          <button type="button" className="btn-ghost mt-2 w-full" onClick={() => setConfirm(false)} disabled={busy}>
            Отмена
          </button>
        </Sheet>
      )}
    </div>
  );
}

function Row({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={strong ? 'font-medium text-ink' : 'text-ink/60'}>{label}</dt>
      <dd className={`tabular-nums ${strong ? 'text-lg font-semibold' : 'text-ink'}`}>{value}</dd>
    </div>
  );
}

// ─── Ждёт отправки ──────────────────────────────────────────────────────────

function PendingView({ body, screen, photoPending }: { body: VisitCloseBody; screen: Screen; photoPending: boolean }) {
  const flushing = useQueue((s) => s.flushing);
  const priceOf = new Map(screen.menu.map((m) => [m.id, m]));
  const delivered = body.lines.reduce((s, l) => s + Math.round((priceOf.get(l.itemId)?.price ?? 0) * l.delivered), 0);
  return (
    <div className="mx-auto w-full max-w-md space-y-3 px-4 py-4">
      <section className="card p-4">
        <div className="flex items-center gap-2 text-amber-800">
          <CloudUpload size={18} />
          <span className="font-medium">Ждёт отправки</span>
        </div>
        <p className="mt-1 text-[13px] text-ink/55">
          Точка сохранена в телефоне{photoPending ? ' вместе с фото витрины' : ''} и уйдёт на сервер, как появится связь.
        </p>
        <dl className="mt-4 space-y-2 text-[15px]">
          <Row label="Отгружено" value={formatKop(delivered)} />
          <Row label="Получено" value={formatKop(body.paidAmount)} />
        </dl>
        <button type="button" className="btn-outline mt-4 w-full" onClick={() => void flushQueue()} disabled={flushing}>
          {flushing && <Loader2 size={18} className="animate-spin" />}
          Отправить сейчас
        </button>
      </section>
    </div>
  );
}
